import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, HeartHandshake, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { API } from "../utils/api";

export default function RequestAdvisorPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    service_type: "",
    preferred_date: "",
    preferred_time: "",
    frequency: "one_off",
    name: "",
    email: "",
    phone: "",
    preferred_contact: "phone",
    notes: "",
  });

  useEffect(() => {
    document.title = "Request a Support Advisor | BTR Health";
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.phone || !form.name) {
      toast.error("Please tell us your name, what support you need and a phone number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/support-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const data = await r.json();
        setSubmitted(data.request_id || "received");
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Couldn't submit. Try again shortly.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#87CEEB]/10 via-white to-[#87CEEB]/5 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-lg p-8 text-center"
          data-testid="advisor-request-success"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Thanks — we've got it</h1>
          <p className="text-slate-600 mb-1">A BTR Health support advisor will be in touch within 1 business day.</p>
          <p className="text-xs text-slate-400 mb-6">Reference: <span className="font-mono text-slate-600">{submitted}</span></p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate("/")} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full h-11">
              Back to home
            </Button>
            <button
              onClick={() => { setSubmitted(null); setForm((f) => ({ ...f, service_type: "", notes: "" })); }}
              className="text-sm text-slate-500 hover:text-slate-900"
            >
              Submit another request
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#87CEEB]/10 via-white to-[#87CEEB]/5">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center max-w-3xl">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm"
            data-testid="advisor-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#87CEEB]/15 text-sky-700 text-xs font-medium mb-4">
            <HeartHandshake className="w-3.5 h-3.5" /> Free — humans, not forms
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            Let us organise the shift for you
          </h1>
          <p className="text-slate-500 mt-2 max-w-md mx-auto">
            Not sure who to book or don't want to message workers directly? A BTR support advisor will find the right person and arrange everything.
          </p>
        </motion.div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10 space-y-5"
          data-testid="advisor-form"
        >
          <div>
            <Label htmlFor="service">What support do you need?</Label>
            <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
              <SelectTrigger id="service" className="mt-1 rounded-xl" data-testid="advisor-service-select">
                <SelectValue placeholder="Choose a service type" />
              </SelectTrigger>
              <SelectContent>
                {["Personal Care","Domestic Assistance","Community Access","Transport","Therapy Support","Social Support","Health & Wellbeing","Not sure — please advise"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Preferred date</Label>
              <Input
                id="date"
                type="date"
                value={form.preferred_date}
                onChange={(e) => update("preferred_date", e.target.value)}
                className="mt-1 rounded-xl"
                data-testid="advisor-date"
              />
            </div>
            <div>
              <Label htmlFor="time">Preferred time</Label>
              <Input
                id="time"
                type="text"
                placeholder="e.g. Mornings, 10am, flexible"
                value={form.preferred_time}
                onChange={(e) => update("preferred_time", e.target.value)}
                className="mt-1 rounded-xl"
                data-testid="advisor-time"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="freq">How often?</Label>
            <Select value={form.frequency} onValueChange={(v) => update("frequency", v)}>
              <SelectTrigger id="freq" className="mt-1 rounded-xl" data-testid="advisor-freq-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="one_off">One-off shift</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="ongoing">Ongoing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="name">Your name</Label>
            <Input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Chris Newman"
              className="mt-1 rounded-xl"
              data-testid="advisor-name"
            />
          </div>

          <div>
            <Label htmlFor="email">Your email (optional)</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com"
              className="mt-1 rounded-xl"
              data-testid="advisor-email"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Your phone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+61 4XX XXX XXX"
                className="mt-1 rounded-xl"
                data-testid="advisor-phone"
              />
            </div>
            <div>
              <Label htmlFor="contact">Contact me by</Label>
              <Select value={form.preferred_contact} onValueChange={(v) => update("preferred_contact", v)}>
                <SelectTrigger id="contact" className="mt-1 rounded-xl" data-testid="advisor-contact-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="sms">Text (SMS)</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Anything else we should know?</Label>
            <Textarea
              id="notes"
              rows={4}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Access needs, language preferences, health conditions, worker preferences…"
              className="mt-1 rounded-xl"
              data-testid="advisor-notes"
            />
          </div>

          <div className="pt-4 flex flex-col-reverse md:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-full md:w-32"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full h-12 text-base"
              data-testid="advisor-submit"
            >
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Free service. No obligation. We respond within 1 business day.
          </p>
        </form>
      </main>
    </div>
  );
}
